import { useState, useEffect } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { useToast } from "@/hooks/use-toast"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { apiRequest } from "@/lib/queryClient"
import type { VisualBrandRequest } from "@shared/schema"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Palette,
  Filter,
  Eye,
  CheckCircle,
  Clock,
  XCircle,
  Loader2,
  Download,
  Image as ImageIcon,
  User,
  Package,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Video,
  RefreshCw
} from "lucide-react"
import { FloatingCard } from "@/components/FloatingCard"
import { AnimatedSection } from "@/components/AnimatedSection"
import { format } from "date-fns"
import { de } from "date-fns/locale"

const statusConfig = {
  submitted: { label: "Eingereicht", color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300", icon: Clock },
  processing: { label: "Wird erstellt", color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300", icon: Loader2 },
  ready: { label: "Fertig", color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300", icon: CheckCircle },
  failed: { label: "Fehlgeschlagen", color: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300", icon: XCircle }
} as const

const modeConfig = {
  person: { label: "Person", icon: User, color: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300" },
  product: { label: "Produkt", icon: Package, color: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300" }
} as const

interface ResultImage {
  id?: string
  path?: string
  filePath?: string
  mimeType?: string
  type?: string
  createdAt?: string
  aspectRatio?: string
  caption?: string
  script?: string
}

// Brand Visuals -> Captioned Video, Slice 1. Mirrors server/routes/brandVideoDto.ts'
// PublicVideoDTO (kept as a local client-side type, NOT imported from server/ — the
// client bundle only resolves the "@" and "@shared" aliases, see vite.config.ts).
// Video state lives HERE, keyed by (sourceImageId, platform) — never on ResultImage,
// since one image can have both a LinkedIn and an Instagram video at once.
type BrandVideoPlatform = "linkedin" | "instagram"

interface PublicVideoDTO {
  id: string
  requestId: string
  sourceImageId: string
  platform: string
  aspect: string
  status: "requested" | "processing" | "ready" | "failed" | string
  error: string | null
  createdAt: string
  updatedAt: string
  downloadUrl: string
}

// The GET list/detail routes additively augment each VisualBrandRequest with these two
// fields (server contract, T5-T9). Both are optional here so older cached query data
// (from before this feature shipped) never throws — see the defensive-access guards below.
type VisualBrandRequestWithVideo = VisualBrandRequest & {
  videos?: PublicVideoDTO[]
  videoEnabled?: boolean
}

export default function BrandVisuals() {
  const [selectedStatus, setSelectedStatus] = useState<string>("all")
  const [selectedMode, setSelectedMode] = useState<string>("all")
  const [selectedRequest, setSelectedRequest] = useState<VisualBrandRequestWithVideo | null>(null)
  const [isGalleryOpen, setIsGalleryOpen] = useState(false)
  const [activeImageIndex, setActiveImageIndex] = useState(0)
  const [deleteConfirmRequest, setDeleteConfirmRequest] = useState<VisualBrandRequestWithVideo | null>(null)
  const [selectedVideoPlatform, setSelectedVideoPlatform] = useState<BrandVideoPlatform>("linkedin")
  const { toast } = useToast()
  const queryClient = useQueryClient()

  // Reset the platform picker to its default when the open request or the active image
  // changes, so a stale platform choice from a previous image/request doesn't carry over.
  useEffect(() => {
    setSelectedVideoPlatform("linkedin")
  }, [selectedRequest?.id, activeImageIndex])

  // Poll if any requests are still processing, OR any request has a video still
  // requested/processing (a video can still be in-flight after its parent request
  // already reached "ready" — without this, the video preview would only show up
  // after a manual page refresh).
  const { data: requests = [], isLoading, error } = useQuery<VisualBrandRequestWithVideo[]>({
    queryKey: ['/api/visual-brand-requests'],
    refetchInterval: (query) => {
      const data = query.state.data as VisualBrandRequestWithVideo[] | undefined
      const hasProcessing = data?.some(req => req.status === 'submitted' || req.status === 'processing')
      const hasProcessingVideo = data?.some(req =>
        req.videos?.some(v => v.status === 'requested' || v.status === 'processing')
      )
      return (hasProcessing || hasProcessingVideo) ? 5000 : false
    }
  })

  const deleteMutation = useMutation({
    mutationFn: async (requestId: string) => {
      await apiRequest('DELETE', `/api/visual-brand-requests/${requestId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/visual-brand-requests'] })
      toast({
        title: "Gelöscht",
        description: "Die Brand Visual Anfrage wurde gelöscht."
      })
      setDeleteConfirmRequest(null)
    },
    onError: (error: Error) => {
      toast({
        title: "Fehler",
        description: error.message || "Löschen fehlgeschlagen.",
        variant: "destructive"
      })
    }
  })

  const createVideoMutation = useMutation({
    mutationFn: async ({ requestId, imageId, platform }: { requestId: string; imageId: string; platform: BrandVideoPlatform }) => {
      return await apiRequest('POST', `/api/visual-brand-requests/${requestId}/video`, { imageId, platform })
    },
    onSuccess: () => {
      // Don't toast success here — the video starts in "requested"/"processing" and the
      // per-platform status block (driven by the poll above) is the feedback surface.
      queryClient.invalidateQueries({ queryKey: ['/api/visual-brand-requests'] })
    },
    onError: (error: Error & { status?: number }) => {
      let description = error.message || "Video konnte nicht erstellt werden."
      if (error.status === 429) {
        description = "Zu viele Videos in Arbeit, bitte kurz warten."
      } else if (error.status === 400) {
        description = "Das Bild oder Skript ist noch nicht bereit für ein Video."
      } else if (error.status === 409) {
        description = "Die Anfrage ist noch nicht fertig."
      } else if (error.status === 503) {
        description = "Video-Erstellung ist momentan nicht verfügbar."
      }
      toast({
        title: "Fehler",
        description,
        variant: "destructive"
      })
    }
  })

  const filteredRequests = requests
    .filter(request => {
      if (selectedStatus !== "all" && request.status !== selectedStatus) return false
      if (selectedMode !== "all" && request.mode !== selectedMode) return false
      return true
    })
    .sort((a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime())

  const handleViewGallery = (request: VisualBrandRequest) => {
    setSelectedRequest(request)
    setActiveImageIndex(0)
    setIsGalleryOpen(true)
  }

  const handleCloseGallery = () => {
    setIsGalleryOpen(false)
    setSelectedRequest(null)
    setActiveImageIndex(0)
  }

  const getImageUrl = (img: ResultImage | string) => {
    const filePath = typeof img === 'string' ? img : (img.path || img.filePath || '')
    if (!filePath) return ''
    if (filePath.startsWith('http')) return filePath
    if (filePath.startsWith('/objects/')) return filePath
    return filePath
  }
  
  const getImagePath = (img: ResultImage) => {
    return img.path || img.filePath || ''
  }

  const handleDownload = async (filePath: string, index: number) => {
    try {
      const url = getImageUrl(filePath)
      const response = await fetch(url)
      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = `brand-visual-${index + 1}.${filePath.split('.').pop() || 'jpg'}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(downloadUrl)
      toast({
        title: "Download gestartet",
        description: `Bild ${index + 1} wird heruntergeladen.`
      })
    } catch (err) {
      toast({
        title: "Download fehlgeschlagen",
        description: "Das Bild konnte nicht heruntergeladen werden.",
        variant: "destructive"
      })
    }
  }

  const formatDate = (date: Date | string | null) => {
    if (!date) return ""
    return format(new Date(date), "dd. MMM yyyy, HH:mm", { locale: de })
  }

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center space-y-4">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-muted-foreground">Lade Brand Visuals...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">Fehler beim Laden der Brand Visuals.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  // The open modal must render from LIVE query data, not the frozen `selectedRequest`
  // snapshot captured at open time — otherwise the 5s poll (and the create mutation's
  // invalidate) refresh `requests` but the modal keeps showing the stale snapshot, so a
  // newly-created video's requested→processing→ready transition never appears until the
  // user closes and reopens. `selectedRequest` stays as the "which modal is open" marker;
  // everything the modal renders reads `liveRequest` (falls back to the snapshot if the
  // request momentarily isn't in the list, e.g. mid-refetch).
  const liveRequest = requests.find((r) => r.id === selectedRequest?.id) ?? selectedRequest
  const resultImages = liveRequest?.resultImages as ResultImage[] | undefined
  const activeImage = resultImages?.[activeImageIndex]
  // Video state for the active image, keyed by (sourceImageId, platform) — never derived
  // from ResultImage. Both a linkedin and an instagram video for the same image can coexist.
  const activeImageVideos = (liveRequest?.videos ?? []).filter(
    (v) => activeImage?.id && v?.sourceImageId === activeImage.id
  )

  return (
    <div className="container mx-auto p-6 space-y-6">
      <AnimatedSection>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3" data-testid="heading-brand-visuals">
              <Palette className="h-8 w-8 text-purple-600" />
              Brand Visuals
            </h1>
            <p className="text-muted-foreground mt-2">
              KI-Markenbilder aus Ihren Fotos
            </p>
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection delay={100}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <FloatingCard>
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Gesamt Anfragen</CardTitle>
              <Palette className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="stat-total-requests">{requests.length}</div>
              <p className="text-xs text-muted-foreground">Brand Visual Anfragen</p>
            </CardContent>
          </FloatingCard>

          <FloatingCard>
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">In Bearbeitung</CardTitle>
              <Loader2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="stat-processing">
                {requests.filter(r => r.status === 'processing' || r.status === 'submitted').length}
              </div>
              <p className="text-xs text-muted-foreground">Werden erstellt</p>
            </CardContent>
          </FloatingCard>

          <FloatingCard>
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Fertig</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="stat-ready">
                {requests.filter(r => r.status === 'ready').length}
              </div>
              <p className="text-xs text-muted-foreground">Bilder bereit</p>
            </CardContent>
          </FloatingCard>

          <FloatingCard>
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Person / Produkt</CardTitle>
              <ImageIcon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="stat-modes">
                {requests.filter(r => r.mode === 'person').length} / {requests.filter(r => r.mode === 'product').length}
              </div>
              <p className="text-xs text-muted-foreground">Nach Modus</p>
            </CardContent>
          </FloatingCard>
        </div>
      </AnimatedSection>

      <AnimatedSection delay={200}>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-[180px]" data-testid="select-status-filter">
                <SelectValue placeholder="Status filtern" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="submitted">Eingereicht</SelectItem>
                <SelectItem value="processing">Wird erstellt</SelectItem>
                <SelectItem value="ready">Fertig</SelectItem>
                <SelectItem value="failed">Fehlgeschlagen</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <ImageIcon className="h-4 w-4 text-muted-foreground" />
            <Select value={selectedMode} onValueChange={setSelectedMode}>
              <SelectTrigger className="w-[180px]" data-testid="select-mode-filter">
                <SelectValue placeholder="Modus filtern" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Modi</SelectItem>
                <SelectItem value="person">Person</SelectItem>
                <SelectItem value="product">Produkt</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection delay={300}>
        {filteredRequests.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Palette className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Keine Brand Visuals gefunden</h3>
              <p className="text-muted-foreground">
                {requests.length === 0 
                  ? "Sie haben noch keine Brand-Visual-Anfragen erstellt."
                  : "Keine Anfragen entsprechen Ihren Filterkriterien."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredRequests.map((request) => {
              const status = statusConfig[request.status as keyof typeof statusConfig] || statusConfig.submitted
              const mode = modeConfig[request.mode as keyof typeof modeConfig] || modeConfig.person
              const StatusIcon = status.icon
              const ModeIcon = mode.icon
              const images = request.resultImages as ResultImage[] | undefined
              const imageCount = images?.length || 0
              
              return (
                <Card 
                  key={request.id} 
                  className="hover-elevate cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                  onClick={() => handleViewGallery(request)}
                  data-testid={`card-brand-request-${request.id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Badge className={mode.color} variant="outline">
                          <ModeIcon className="h-3 w-3 mr-1" />
                          {mode.label}
                        </Badge>
                      </div>
                      <Badge className={status.color}>
                        <StatusIcon className={`h-3 w-3 mr-1 ${request.status === 'processing' ? 'animate-spin' : ''}`} />
                        {status.label}
                      </Badge>
                    </div>
                    <CardTitle className="text-base mt-2" data-testid={`request-title-${request.id}`}>
                      {request.mainPhotoName}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {request.status === 'ready' && images && images.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2">
                        {images.slice(0, 3).map((img, idx) => (
                          <div key={img?.id || idx} className="relative aspect-square bg-muted rounded-lg overflow-hidden">
                            <img
                              src={getImageUrl(img)}
                              alt={`Brand Visual ${idx + 1}`}
                              className="w-full h-full object-cover"
                              data-testid={`card-image-${request.id}-${idx}`}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
                        {request.status === 'processing' || request.status === 'submitted' ? (
                          <div className="text-center">
                            <Loader2 className="h-8 w-8 text-muted-foreground mx-auto mb-2 animate-spin" />
                            <p className="text-sm text-muted-foreground">Wird erstellt...</p>
                          </div>
                        ) : request.status === 'failed' ? (
                          <div className="text-center">
                            <XCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground">Fehler bei der Erstellung</p>
                          </div>
                        ) : (
                          <ImageIcon className="h-8 w-8 text-muted-foreground" />
                        )}
                      </div>
                    )}
                    
                    {request.message && (
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {request.message}
                      </p>
                    )}

                    <div className="flex items-center justify-between pt-2 border-t gap-2">
                      <span className="text-xs text-muted-foreground">
                        {formatDate(request.createdAt)}
                      </span>
                      <div className="flex items-center gap-1">
                        {request.status === 'ready' && imageCount > 0 && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            data-testid={`button-view-gallery-${request.id}`}
                            onClick={(e) => {
                              e.stopPropagation()
                              handleViewGallery(request)
                            }}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            {imageCount} Bilder
                          </Button>
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          data-testid={`button-delete-${request.id}`}
                          onClick={(e) => {
                            e.stopPropagation()
                            setDeleteConfirmRequest(request)
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}
      </AnimatedSection>

      <Dialog open={isGalleryOpen} onOpenChange={(open) => !open && handleCloseGallery()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5 text-purple-600" />
              Brand Visuals Galerie
            </DialogTitle>
          </DialogHeader>
          
          {liveRequest && resultImages && resultImages.length > 0 && (
            <div className="space-y-6">
              <div className="relative aspect-video bg-muted rounded-lg overflow-hidden">
                <img
                  src={getImageUrl(resultImages[activeImageIndex])}
                  alt={`Brand Visual ${activeImageIndex + 1}`}
                  className="w-full h-full object-contain"
                  data-testid="gallery-main-image"
                />
                
                {resultImages.length > 1 && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-background/80 hover:bg-background"
                      onClick={() => setActiveImageIndex((prev) => (prev - 1 + resultImages.length) % resultImages.length)}
                      data-testid="button-gallery-prev"
                    >
                      <ChevronLeft className="h-6 w-6" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-background/80 hover:bg-background"
                      onClick={() => setActiveImageIndex((prev) => (prev + 1) % resultImages.length)}
                      data-testid="button-gallery-next"
                    >
                      <ChevronRight className="h-6 w-6" />
                    </Button>
                  </>
                )}
                
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-background/80 px-3 py-1 rounded-full text-sm">
                  {activeImageIndex + 1} / {resultImages.length}
                </div>
              </div>
              
              {resultImages[activeImageIndex]?.caption && (
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm font-medium mb-1">Ad Copy:</p>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap" data-testid="gallery-caption">
                    {resultImages[activeImageIndex].caption}
                  </p>
                  {resultImages[activeImageIndex]?.aspectRatio && (
                    <Badge variant="secondary" className="mt-2">
                      {resultImages[activeImageIndex].aspectRatio}
                    </Badge>
                  )}
                </div>
              )}

              {activeImage?.script && (
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm font-medium mb-1">Skript:</p>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap" data-testid="gallery-script">
                    {activeImage.script}
                  </p>
                </div>
              )}

              <div className="flex items-center justify-center gap-2">
                {resultImages.map((img, idx) => (
                  <button
                    key={img?.id || idx}
                    onClick={() => setActiveImageIndex(idx)}
                    className={`relative w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      idx === activeImageIndex 
                        ? 'border-purple-600 ring-2 ring-purple-600/20' 
                        : 'border-transparent hover:border-muted-foreground/50'
                    }`}
                    data-testid={`button-thumbnail-${idx}`}
                  >
                    <img
                      src={getImageUrl(img)}
                      alt={`Thumbnail ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>

              <div className="flex items-center justify-center gap-4">
                <Button
                  variant="outline"
                  onClick={() => handleDownload(getImagePath(resultImages[activeImageIndex]), activeImageIndex)}
                  data-testid="button-download-image"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Bild herunterladen
                </Button>
              </div>

              {/* Als Video erstellen — ONLY when the server reports videoEnabled for this
                  request's company (canary + kill switch). Never a client-side constant. */}
              {liveRequest.videoEnabled && (
                <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                  <p className="text-sm font-medium">Video erstellen</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <Select
                      value={selectedVideoPlatform}
                      onValueChange={(value) => setSelectedVideoPlatform(value as BrandVideoPlatform)}
                    >
                      <SelectTrigger className="w-[160px]" data-testid="select-video-platform">
                        <SelectValue placeholder="Plattform" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="linkedin">LinkedIn</SelectItem>
                        <SelectItem value="instagram">Instagram</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={() => {
                        if (!liveRequest?.id || !activeImage?.id) return
                        createVideoMutation.mutate({
                          requestId: liveRequest.id,
                          imageId: activeImage.id,
                          platform: selectedVideoPlatform,
                        })
                      }}
                      disabled={createVideoMutation.isPending || !activeImage?.id}
                      data-testid="button-create-video"
                    >
                      {createVideoMutation.isPending ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Wird erstellt...
                        </>
                      ) : (
                        <>
                          <Video className="h-4 w-4 mr-2" />
                          Als Video erstellen
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Per-platform video status for the active image — a linkedin AND an
                  instagram video can coexist, so each renders as its own row. */}
              {activeImageVideos.length > 0 && (
                <div className="space-y-3">
                  {activeImageVideos.map((video) => (
                    <div
                      key={video.id}
                      className="border rounded-lg p-3 space-y-2"
                      data-testid={`video-status-${video.platform}`}
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline">
                          {video.platform === 'linkedin' ? 'LinkedIn' : video.platform === 'instagram' ? 'Instagram' : video.platform}
                        </Badge>
                        {(video.status === 'requested' || video.status === 'processing') && (
                          <span className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Video wird erstellt…
                          </span>
                        )}
                        {video.status === 'failed' && (
                          <span className="text-sm text-destructive">
                            {video.error || "Video konnte nicht erstellt werden."}
                          </span>
                        )}
                      </div>

                      {video.status === 'ready' && video.downloadUrl && (
                        <div className="space-y-2">
                          <video
                            src={video.downloadUrl}
                            controls
                            className="w-full max-h-64 rounded-lg bg-black"
                            data-testid={`video-player-${video.platform}`}
                          />
                          <a
                            href={video.downloadUrl}
                            download
                            onClick={() => {
                              toast({
                                title: "Download gestartet",
                                description: "Das Video wird heruntergeladen.",
                              });
                            }}
                          >
                            <Button variant="outline" size="sm" data-testid={`button-download-video-${video.platform}`}>
                              <Download className="h-4 w-4 mr-2" />
                              Video herunterladen
                            </Button>
                          </a>
                        </div>
                      )}

                      {video.status === 'failed' && liveRequest.videoEnabled && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (!liveRequest?.id) return
                            createVideoMutation.mutate({
                              requestId: liveRequest.id,
                              imageId: video.sourceImageId,
                              platform: (video.platform === 'instagram' ? 'instagram' : 'linkedin') as BrandVideoPlatform,
                            })
                          }}
                          disabled={createVideoMutation.isPending}
                          data-testid={`button-retry-video-${video.platform}`}
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Erneut versuchen
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {liveRequest.message && (
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm font-medium mb-1">Ihre Anfrage:</p>
                  <p className="text-sm text-muted-foreground">{liveRequest.message}</p>
                </div>
              )}

              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  {modeConfig[liveRequest.mode as keyof typeof modeConfig] && (
                    <>
                      {liveRequest.mode === 'person' ? <User className="h-4 w-4" /> : <Package className="h-4 w-4" />}
                      <span>{modeConfig[liveRequest.mode as keyof typeof modeConfig]?.label}</span>
                    </>
                  )}
                </div>
                <span>|</span>
                <span>Erstellt: {formatDate(liveRequest.createdAt)}</span>
              </div>
            </div>
          )}

          {liveRequest && (!resultImages || resultImages.length === 0) && (
            <div className="text-center py-12">
              <ImageIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Keine Bilder verfügbar.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteConfirmRequest} onOpenChange={(open) => !open && setDeleteConfirmRequest(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Anfrage löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie diese Brand-Visual-Anfrage wirklich löschen? 
              {deleteConfirmRequest?.status === 'ready' && " Alle generierten Bilder werden ebenfalls gelöscht."}
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteConfirmRequest && deleteMutation.mutate(deleteConfirmRequest.id)}
              disabled={deleteMutation.isPending}
              data-testid="button-confirm-delete"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Löschen...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Löschen
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
